import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Upload } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { useAuth, can } from "../../lib/auth";
import { useLang } from "../../lib/i18n";
import { EmployeeDialog } from "./EmployeeDialog";
import { EmployeeTable } from "./EmployeeTable";
import { ImportDialog } from "./ImportDialog";
import { BulkBar } from "./BulkBar";

const Stat = ({ label, value, testId, accent, selected, onClick }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={selected}
    className={`btn-press flex items-center gap-2 rounded-full border bg-white px-4 py-2 transition-colors duration-200 hover:border-slate-900 ${
      selected ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200/80"}`}>
    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
    <span className={`font-display text-lg font-extrabold ${accent || "text-slate-900"}`}>{value}</span>
  </button>
);

const EMPTY_TEXT = { all: "No employees yet.", active: "No active employees.", inactive: "No inactive employees." };

export const EmployeesPanel = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ domain: "", fields: [], roles: [], templates: [] });
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [dialog, setDialog] = useState({ open: false, employee: null });
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const CONFIRM = { delete: "Delete {count} employees permanently? This can't be undone.", suspend: "Disable {count} employees?",
    restore: "Enable {count} employees?", invite: "Send invite emails to {count} employees?" };
  const runBulk = async (action) => {
    if (!window.confirm(t(CONFIRM[action], { count: selected.size }))) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/admin/employees/bulk", { ids: [...selected], action });
      if (r.done) toast.success(t("{count} done", { count: r.done }));
      if (r.failed.length) toast.error(t("{count} failed", { count: r.failed.length }) + ": " + t(r.failed[0].error));
      setSelected(new Set());
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBulkBusy(false); }
  };

  const load = useCallback(() => {
    api.get("/admin/employees").then(({ data }) => setData(data)).catch((e) => toast.error(errorMessage(e)));
  }, []);
  useEffect(() => {
    load();
    Promise.all([api.get("/auth/config"), api.get("/fields"), api.get("/admin/roles"),
      api.get("/admin/templates").catch(() => ({ data: { templates: [] } }))]).then(([c, f, r, t]) =>
      setMeta({ domain: c.data.allowed_domain, fields: f.data.fields, roles: r.data.roles, templates: t.data.templates }));
  }, [load]);

  const openCreate = () => {
    api.get("/admin/templates").then(({ data }) => setMeta((m) => ({ ...m, templates: data.templates }))).catch(() => {});
    setDialog({ open: true, employee: null });
  };

  const toggleActive = async (emp) => {
    try {
      await api.patch(`/admin/employees/${emp.id}`, { is_active: !emp.is_active });
      toast.success(t(emp.is_active ? "Card deactivated" : "Card activated"));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const remove = async (emp) => {
    if (!window.confirm(t("Delete {name} permanently? Their card link will stop working and this can't be undone.", { name: emp.name_en }))) return;
    try {
      await api.delete(`/admin/employees/${emp.id}`);
      toast.success(t("Employee deleted"));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const invite = async (emp) => {
    try {
      await api.post(`/admin/employees/${emp.id}/invite`);
      toast.success(t("Invite email sent to {email}", { email: emp.email }));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!data) return [];
    return data.employees.filter((e) => status === "all" || e.is_active === (status === "active")).filter((e) => !q || [e.name_en, e.name_ar, e.email, e.title_en].some((v) => (v || "").toLowerCase().includes(q)));
  }, [data, query, status]);

  return (
    <div className="space-y-4" data-testid="employees-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Stat label={t("Total")} value={data?.stats.total ?? "–"} testId="stat-total" selected={status === "all"} onClick={() => setStatus("all")} />
        <Stat label={t("Active")} value={data?.stats.active ?? "–"} testId="stat-active" accent="text-emerald-600" selected={status === "active"} onClick={() => setStatus("active")} />
        <Stat label={t("Inactive")} value={data?.stats.inactive ?? "–"} testId="stat-inactive" accent="text-slate-400" selected={status === "inactive"} onClick={() => setStatus("inactive")} />
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Search name, email, title")} data-testid="employee-search-input"
            className="h-10 w-full rounded-full border border-slate-200 bg-white pe-4 ps-9 text-sm outline-none focus:border-slate-900" />
        </div>
        <p className="me-auto text-xs text-slate-500" data-testid="admin-access-note">
          {t("Sign-in restricted to")} <span dir="ltr" className="font-mono">@{meta.domain}</span>
        </p>
        {can(user, "employees.import") && can(user, "employees.create") && (
          <button onClick={() => setImportOpen(true)} data-testid="import-employees-button"
            className="btn-press inline-flex min-h-[40px] items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 hover:border-slate-900">
            <Upload className="h-4 w-4" /> {t("Import CSV")}
          </button>
        )}
        {can(user, "employees.create") && (
          <button onClick={openCreate} data-testid="add-employee-button"
            className="btn-press inline-flex min-h-[40px] items-center gap-2 rounded-full bg-cobalt px-4 text-sm font-semibold text-white hover:bg-cobalt-hover">
            <Plus className="h-4 w-4" /> {t("Add employee")}
          </button>
        )}
      </div>
      <BulkBar count={selected.size} busy={bulkBusy} onAction={runBulk} onClear={() => setSelected(new Set())}
        perms={{ invite: can(user, "employees.create"), toggle: can(user, "employees.deactivate"), delete: can(user, "employees.delete") }} />
      <EmployeeTable employees={filtered} loading={!data} roles={meta.roles} currentEmail={user.email}
        selected={selected} onSelect={toggleOne}
        onSelectAll={(v) => setSelected(v ? new Set(filtered.filter((e) => e.email !== user.email).map((e) => e.id)) : new Set())}
        canEdit={can(user, "employees.edit") || can(user, "employees.assign_role")} canToggle={can(user, "employees.deactivate")}
        onEdit={(e) => setDialog({ open: true, employee: e })} onToggle={toggleActive}
        canDelete={can(user, "employees.delete")} onDelete={remove}
        canInvite={can(user, "employees.create")} onInvite={invite}
        emptyText={t(EMPTY_TEXT[status])} />
      <EmployeeDialog open={dialog.open} employee={dialog.employee} meta={meta} onSaved={load}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
};
